import { useEffect, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MapPin, Building, LogIn, LogOut, Download, Search, CheckCircle2, Camera, X, Trash2 } from "lucide-react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function getPosition() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
      () => resolve(null),
      { timeout: 8000 }
    );
  });
}

export default function Attendance() {
  const { user } = useAuth();
  const now = new Date();
  const [today, setToday] = useState(null);
  const [mode, setMode] = useState("WFO");
  const [records, setRecords] = useState([]);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [q, setQ] = useState("");
  const [selfie, setSelfie] = useState("");
  const [busy, setBusy] = useState(false);
  const [viewSelfie, setViewSelfie] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [leaveForm, setLeaveForm] = useState({ leave_type: "Sick", start_date: "", end_date: "", reason: "" });
  const isManager = user.role === "SPV" || user.role === "SuperAdmin";

  const loadLeaves = () => api.get("/leaves").then((r) => setLeaves(r.data));
  useEffect(() => { loadLeaves(); }, []);
  const submitLeave = async () => {
    if (!leaveForm.start_date || !leaveForm.end_date) return toast.error("Pick start & end dates");
    try { await api.post("/leaves", leaveForm); toast.success("Leave request submitted"); setLeaveForm({ leave_type: "Sick", start_date: "", end_date: "", reason: "" }); loadLeaves(); }
    catch (e) { toast.error("Failed"); }
  };
  const reviewLeave = async (id, approve) => { await api.post(`/leaves/${id}/review`, { approve }); toast.success(approve ? "Approved" : "Rejected"); loadLeaves(); };
  const delLeave = async (id) => { await api.delete(`/leaves/${id}`); loadLeaves(); };

  const loadToday = () => api.get("/attendance/today").then((r) => setToday(r.data));
  const loadRecords = () => api.get(`/attendance?month=${month}&year=${year}`).then((r) => setRecords(r.data));
  useEffect(() => { loadToday(); const t = setInterval(() => { loadToday(); loadRecords(); }, 20000); return () => clearInterval(t); }, []);
  useEffect(() => { loadRecords(); }, [month, year]);

  const checkin = async () => {
    setBusy(true);
    try {
      const pos = await getPosition();
      let location_name = "";
      if (pos) location_name = `${pos.latitude.toFixed(5)}, ${pos.longitude.toFixed(5)}`;
      if (mode === "WFA" && !pos && !selfie) {
        toast.error("Enable location or add a selfie for WFA check-in");
        setBusy(false); return;
      }
      await api.post("/attendance/checkin", { mode, latitude: pos?.latitude, longitude: pos?.longitude, location_name, selfie });
      toast.success(`Checked in — ${mode}`); setSelfie(""); loadToday(); loadRecords();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const checkout = async () => {
    try { await api.post("/attendance/checkout"); toast.success("Checked out"); loadToday(); loadRecords(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openSelfie = async (r) => {
    try { const res = await api.get(`/attendance/${r.id}/selfie`, { responseType: "blob" }); setViewSelfie(URL.createObjectURL(res.data)); }
    catch (e) { toast.error("No selfie"); }
  };

  const filtered = records.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[var(--gold)] text-xs uppercase tracking-[0.2em] font-semibold">Feature 4</div>
        <h1 className="font-serif text-4xl mt-1">Attendance Record</h1>
        <p className="text-[var(--text-secondary)] mt-1">Choose your work mode, then check in. Time is recorded in WIB.</p>
      </div>

      <div className="card-elegant p-7">
        {today?.checkin_at && !today?.checkout_at ? (
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center"><CheckCircle2 className="w-6 h-6 text-[var(--teal)]" /></div>
              <div>
                <div className="font-semibold">You are checked in — <span style={{ color: today.mode === "WFA" ? "#0D9488" : "#C5A059" }}>{today.mode}</span></div>
                <div className="text-sm text-[var(--text-secondary)] font-mono">Since {today.checkin_time} WIB · {today.date}</div>
              </div>
            </div>
            <button data-testid="attendance-checkout-button" onClick={checkout} className="px-6 py-3 rounded-xl bg-[var(--obsidian)] text-white font-semibold flex items-center gap-2 hover:opacity-90 transition">
              <LogOut className="w-4 h-4" /> Check Out
            </button>
          </div>
        ) : today?.checkout_at ? (
          <div className="text-center py-4">
            <CheckCircle2 className="w-10 h-10 text-[var(--complete)] mx-auto mb-2" />
            <div className="font-semibold">Attendance complete for today.</div>
            <div className="text-sm text-[var(--text-secondary)] font-mono mt-1">
              {today.mode} · {today.checkin_time} → {today.checkout_time} · {today.total_hours} hrs
            </div>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="text-sm font-semibold text-[var(--text-secondary)] mb-3">Select work mode</div>
              <div className="flex gap-3">
                {[
                  { id: "WFO", label: "Work From Office", icon: Building, testid: "attendance-wfo-radio" },
                  { id: "WFA", label: "Work From Anywhere", icon: MapPin, testid: "attendance-wfa-radio" },
                ].map((m) => (
                  <button key={m.id} data-testid={m.testid} onClick={() => setMode(m.id)}
                    className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 transition ${mode === m.id ? "border-[var(--gold)] bg-[var(--gold-light)]" : "border-[var(--border-subtle)]"}`}>
                    <m.icon className={`w-5 h-5 ${mode === m.id ? "text-[var(--gold)]" : "text-[var(--text-muted)]"}`} />
                    <span className="text-sm font-medium">{m.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <button data-testid="attendance-checkin-button" onClick={checkin} disabled={busy} className="btn-gold px-8 py-3.5 flex items-center gap-2 text-base disabled:opacity-60">
              <LogIn className="w-5 h-5" /> {busy ? "Checking in…" : "Check In"}
            </button>
          </div>
        )}
        {!today?.checkin_at && (
          <div className="mt-4 pt-4 border-t border-[var(--border-subtle)] flex items-center gap-3">
            <label className="text-sm text-[var(--text-secondary)] flex items-center gap-2 cursor-pointer">
              <Camera className="w-4 h-4 text-[var(--gold)]" />
              <span>{mode === "WFA" ? "Selfie (recommended for WFA)" : "Selfie (optional)"}</span>
              <input data-testid="attendance-selfie-input" type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => {
                const f = e.target.files?.[0]; if (!f) return;
                if (f.size > 2 * 1024 * 1024) { toast.error("Photo must be under 2MB"); return; }
                const rd = new FileReader(); rd.onload = () => setSelfie(rd.result); rd.readAsDataURL(f);
              }} />
              <span className="btn-gold px-3 py-1.5 text-xs">Take / Upload</span>
            </label>
            {selfie && <img src={selfie} alt="selfie" className="w-10 h-10 rounded-lg object-cover border border-[var(--border-gold)]" />}
            <span className="text-xs text-[var(--text-muted)]">Location is captured automatically on check-in.</span>
          </div>
        )}
      </div>

      {viewSelfie && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(17,19,24,0.6)" }} onClick={() => setViewSelfie(null)}>
          <div className="relative"><img src={viewSelfie} alt="selfie" className="max-h-[80vh] rounded-xl" /><button className="absolute -top-3 -right-3 bg-white rounded-full p-1"><X className="w-5 h-5" /></button></div>
        </div>
      )}

      {/* Leave / Sick Leave */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card-elegant p-6">
          <h3 className="font-serif text-xl mb-4">Request Leave / Sick Leave</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)]">Type</label>
              <select data-testid="leave-type" value={leaveForm.leave_type} onChange={(e) => setLeaveForm({ ...leaveForm, leave_type: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] bg-white text-sm outline-none">
                {["Sick", "Annual", "Permission"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div />
            <div><label className="text-xs font-semibold text-[var(--text-secondary)]">From</label><input data-testid="leave-start" type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none" /></div>
            <div><label className="text-xs font-semibold text-[var(--text-secondary)]">To</label><input data-testid="leave-end" type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none" /></div>
          </div>
          <textarea data-testid="leave-reason" value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} rows="2" placeholder="Reason…" className="mt-3 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none" />
          <button data-testid="leave-submit" onClick={submitLeave} className="btn-gold mt-3 px-5 py-2 text-sm">Submit Request</button>
        </div>
        <div className="card-elegant p-6">
          <h3 className="font-serif text-xl mb-4">{isManager ? "Leave Approvals" : "My Leave Requests"}</h3>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {leaves.map((l) => (
              <div key={l.id} data-testid={`leave-row-${l.id}`} className="flex items-center justify-between bg-[var(--bg-subtle)] rounded-lg px-3 py-2 text-sm">
                <div>
                  <span className="font-medium">{l.name}</span> <span className="text-xs px-2 py-0.5 rounded bg-white">{l.leave_type}</span>
                  <div className="text-xs text-[var(--text-secondary)]">{l.start_date} → {l.end_date} · {l.reason || "—"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold ${l.status === "Approved" ? "text-[var(--complete)]" : l.status === "Rejected" ? "text-[var(--urgent)]" : "text-[var(--pending)]"}`}>{l.status}</span>
                  {isManager && l.status === "Pending" && (
                    <>
                      <button data-testid={`leave-approve-${l.id}`} onClick={() => reviewLeave(l.id, true)} className="text-[var(--complete)]"><CheckCircle2 className="w-4 h-4" /></button>
                      <button data-testid={`leave-reject-${l.id}`} onClick={() => reviewLeave(l.id, false)} className="text-[var(--urgent)]"><X className="w-4 h-4" /></button>
                    </>
                  )}
                  {(isManager || l.status === "Pending") && <button onClick={() => delLeave(l.id)} className="text-[var(--text-muted)] hover:text-[var(--urgent)]"><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              </div>
            ))}
            {leaves.length === 0 && <div className="text-sm text-[var(--text-muted)] py-4 text-center">No leave requests.</div>}
          </div>
        </div>
      </div>

      {isManager && (
      <>
      <div className="card-elegant p-6">
        <h3 className="font-serif text-xl mb-4">Attendance Diagram — {MONTHS[month - 1]} {year}</h3>
        <div className="space-y-3">
          {Object.entries(filtered.reduce((acc, r) => { acc[r.name] = (acc[r.name] || 0) + 1; return acc; }, {})).sort((a, b) => b[1] - a[1]).map(([name, cnt]) => (
            <div key={name}>
              <div className="flex justify-between text-sm mb-1"><span>{name}</span><span className="font-mono">{cnt} days</span></div>
              <div className="h-2.5 rounded-full bg-[var(--bg-subtle)] overflow-hidden"><div className="h-full rounded-full gold-gradient" style={{ width: `${Math.min(100, (cnt / 22) * 100)}%` }} /></div>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-sm text-[var(--text-muted)]">No attendance data this month.</div>}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-serif text-xl">Monthly Log</h3>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
            <input data-testid="attendance-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff…" className="pl-9 pr-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none focus:border-[var(--gold)] w-44" />
          </div>
          <select value={month} onChange={(e) => setMonth(+e.target.value)} className="px-3 py-2 rounded-lg border border-[var(--border-subtle)] bg-white text-sm outline-none">
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(+e.target.value)} className="px-3 py-2 rounded-lg border border-[var(--border-subtle)] bg-white text-sm outline-none">
            {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button data-testid="export-attendance-button" onClick={() => downloadFile(`/export/attendance?month=${month}&year=${year}`, `${String(month).padStart(2,"0")} - SV Attendance ${year}.xlsx`)} className="btn-gold px-4 py-2 text-sm flex items-center gap-2"><Download className="w-4 h-4" /> Excel</button>
        </div>
      </div>

      <div className="card-elegant overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[var(--text-muted)] text-xs uppercase bg-[var(--bg-subtle)]">
              <th className="py-3 px-5">Date</th><th className="py-3 px-5">Name</th><th>ID</th><th>Mode</th><th>Check In</th><th>Check Out</th><th>Hours</th><th>Location</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-[var(--border-subtle)] hover:bg-[var(--gold-light)]/40">
                <td className="py-3 px-5 font-mono text-xs">{r.date}</td>
                <td className="font-medium">{r.name}</td>
                <td className="font-mono text-xs">{r.id_code}</td>
                <td><span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ color: r.mode === "WFA" ? "#0D9488" : "#C5A059", background: r.mode === "WFA" ? "#CCFBF1" : "#F9F6EE" }}>{r.mode}</span></td>
                <td className="font-mono text-xs">{r.checkin_time}</td>
                <td className="font-mono text-xs">{r.checkout_time || "—"}</td>
                <td className="font-mono text-xs">{r.total_hours ?? "—"}</td>
                <td className="text-xs">
                  <div className="flex items-center gap-2">
                    {r.location_name ? <a href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`} target="_blank" rel="noreferrer" className="text-[var(--teal)] hover:underline flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{r.location_name}</a> : <span className="text-[var(--text-muted)]">—</span>}
                    {r.has_selfie && <button data-testid={`view-selfie-${r.id}`} onClick={() => openSelfie(r)} className="text-[var(--gold)]"><Camera className="w-4 h-4" /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="8" className="py-10 text-center text-[var(--text-muted)]">No records for this period.</td></tr>}
          </tbody>
        </table>
      </div>
      </>
      )}
    </div>
  );
}
